import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { catchError, tap, of } from 'rxjs';
import { AuthService, LoadingService, NotificationService } from 'src/app/core';
import { Department, Machine } from 'src/app/modules/shared/interface/department.entity';
import { DepartmentService } from 'src/app/modules/shared/services/department.service';
import { JobOperationService } from 'src/app/modules/shared/services/job-operation.service';
import { ModalService } from 'src/app/modules/shared/services/modal.service';
import { MachineService } from 'src/app/modules/shared/services/machine.service';
import { GetJobOperationDTO, MachineChangeDTO, MachineRescheduleDTO, RescheduleJobOperationDTO, RescheduledHoldReasonDTO } from 'src/app/modules/shared/interface/jop-operation.entity';
import { GetMachineDTO } from 'src/app/modules/shared/interface/machine.entity';
import { GlobalDepartmentService } from 'src/app/core/services/global-department.service';
import { JobCardService } from 'src/app/modules/job-operation/services/job-card.service';
import { JobCardFindDTO } from 'src/app/modules/job-operation/interfaces/job-card.entity';
import { PlanningViewComponent } from '../planning-view/planning-view.component';

export interface TimelineItem {
  type: 'free' | 'occupied';
  startTime: string;
  endTime: string;
  startMs: number;
  durationHours: number;
  startFullDateTime: string;
  jobNo?: string;
  jobStatus?: string;
  estimatedDateTime?: string;
  estimateTime?: number;
}

@Component({
  selector: 'app-machine-timeline',
  imports: [
    CommonModule,
    FormsModule,
    PlanningViewComponent
  ],
  templateUrl: './operation-schedule.component.html',
  styleUrl: './operation-schedule.component.scss'
})
export class OperationScheduleComponent {
  notification = inject(NotificationService);
  loading = inject(LoadingService);
  departmentService = inject(DepartmentService);
  jobOperationService = inject(JobOperationService);
  modal = inject(ModalService);
  machineService = inject(MachineService);
  authService = inject(AuthService);
  globalDepartmentService = inject(GlobalDepartmentService);
  jobCardService = inject(JobCardService);

  protected Department = signal<Department[]>([]);
  protected DepartmentNew = signal<Department[]>([]);
  protected $$departmentDTO = signal<Department[]>([]);
  protected $$MachineDTO = signal<GetMachineDTO[]>([]);
  protected GetJobOperationDTO = signal<GetJobOperationDTO[]>([]);
  protected RescheduleJobOperationDTO = signal<RescheduleJobOperationDTO[]>([]);
  protected rescheduledHoldReason = signal<RescheduledHoldReasonDTO | null>(null);
  department_id = this.globalDepartmentService.selectedDepartmentId;
  machineId: number | null = null;
  newEstimateTime: number | null = null;
  newEstimateDateTime: string = '';
  machineChangeEstimateDateTime: string = '';
  estimateDateTime: string = '';
  minDateTime: string = this.getDayAfterTomorrowISOString();
  minDateTimeMachine: string = '';
  mode: 'reschdule' | 'machineChange' = 'reschdule';
  start_date: string = '';
  end_date: string = '';
  manualStartDate: string = '';
  manualEndDate: string = '';
  showCustomRange = signal(false);
  selectedPlannedQuantity: string | number | null = null;

  protected selectedMachineId = signal<number | null>(null);
  searchJobNo = signal<string>('');
  
  isEditMode = signal(false);
  draggedOperation: any = null;

  isJobHighlighted(jobNo: string): boolean {
    const search = this.searchJobNo()?.trim();
    if (!search || search.length < 3) return false;
    if (!jobNo) return false;
    return jobNo.toLowerCase().includes(search.toLowerCase());
  }

  maintenanceMachineId: number | null = null;
  maintenanceEstimatedDateTime: string = '';
  maintenanceEstimateTime: number | null = null;
  maintenanceRemarks: string = '';

  cancelMaintenanceRemarks: string = '';
  cancelMaintenanceId: number | null = null;

  protected MachineOperations = signal<any[]>([]);
  protected FilteredOperations = signal<any[]>([]);
  protected MachineChangeFilteredOperations = signal<any[]>([]);
  protected MaintenanceFilteredOperations = signal<any[]>([]);

  selectedYear = signal(new Date().getFullYear());
  selectedMonth = signal(new Date().getMonth());
  calendarDays: Date[] = [];
  months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  years: number[] = [];
  timeSlots: string[] = []; // Array of 24 hours (00:00 to 23:00)
  selectedYearValue: number = this.selectedYear();
  selectedMonthValue: number = this.selectedMonth();
  showOperationReschedule = signal(false);

  protected startDayIndex = signal(0);
  private today = new Date();

  private startOfWeek = new Date(
    new Date().setDate(new Date().getDate() - new Date().getDay())
  );

  private endOfWeek = new Date(
    new Date(this.startOfWeek).setDate(this.startOfWeek.getDate() + 6)
  );

  private formatDate(date: string | Date): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-CA');
  }

  protected searchParams = signal<{
    id: number;
    machine: number | null;
    start_date: string;
    end_date: string;
  }>({
    id: this.globalDepartmentService.getDepartmentId() ?? -1,
    machine: null,
    start_date: this.formatDate(this.startOfWeek),
    end_date: this.formatDate(this.endOfWeek)
  });

  protected searchParamsForMachine = signal({
    id: this.globalDepartmentService.getDepartmentId() ?? -1,
    machine: this.machineId,
    start_date: '',
    end_date: ''
  });

  constructor() {
    this.jobCardService.getRefreshObservable().subscribe(() => {
      this.get();
    });
    this.initializeCurrentWeek();
    this.fetchDepartment();
    this.populateYears();
    this.generateTimeSlots();
  }


  ngOnInit(): void {
    const now = new Date();
    now.setSeconds(0, 0);
    this.minDateTimeMachine = now.toISOString().slice(0, 16);
  }

  getDayAfterTomorrowISOString(): string {
    const date = new Date();
    date.setDate(date.getDate() + 2);
    date.setSeconds(0);
    date.setMilliseconds(0);

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }

  populateYears(): void {
    const startYear = 2025;
    this.years = Array.from({ length: 11 }, (_, i) => startYear + i);
  }

  generateCalendar(): void {
    const year = this.selectedYear();
    const month = this.selectedMonthValue;
    const daysInMonthMap = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    if (month === 1) {
      const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
      daysInMonthMap[1] = isLeapYear ? 29 : 28;
    }

    const daysInMonth = daysInMonthMap[month];

    if (this.startDayIndex() + 7 > daysInMonth) {
      this.startDayIndex.set(Math.max(0, daysInMonth - 7));
    }

    this.calendarDays = Array.from(
      { length: 7 },
      (_, i) => new Date(year, month, this.startDayIndex() + i + 1)
    );
  }

  initializeCurrentWeek(): void {
    const today = new Date();
    this.selectedYear.set(today.getFullYear());
    this.selectedMonth.set(today.getMonth());
    this.selectedYearValue = today.getFullYear();
    this.selectedMonthValue = today.getMonth();

    const currentDate = today.getDate();
    const startIndex = Math.floor((currentDate - 1) / 7) * 7;
    this.startDayIndex.set(startIndex);

    this.generateCalendar(); // calendarDays is now set correctly

    // Now derive searchParams from the actual rendered calendar days
    const format = (d: Date) => d.toLocaleDateString('en-CA');
    this.searchParams.set({
      id: this.department_id() ?? -1,
      machine: this.selectedMachineId(),
      start_date: format(this.calendarDays[0]),
      end_date: format(this.calendarDays[this.calendarDays.length - 1])
    });
  }

  toggleCustomRange(): void {
    this.showCustomRange.set(!this.showCustomRange());
    if (!this.showCustomRange()) {
      this.initializeCurrentWeek();
      this.get();
    } else {
      if (this.calendarDays.length > 0) {
        this.manualStartDate = this.formatDate(this.calendarDays[0]);
        this.manualEndDate = this.formatDate(this.calendarDays[this.calendarDays.length - 1]);
      }
    }
  }

  applyManualDateRange(): void {
    if (!this.manualStartDate || !this.manualEndDate) {
      this.notification.set({ type: 'error', message: 'Please select both start and end dates' });
      return;
    }

    const start = new Date(this.manualStartDate);
    const end = new Date(this.manualEndDate);

    if (end < start) {
      this.notification.set({ type: 'error', message: 'End date cannot be before start date' });
      return;
    }

    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 6) {
      this.notification.set({ type: 'error', message: 'Date range cannot exceed 7 days' });
      return;
    }

    this.calendarDays = [];
    let current = new Date(start);
    while (current <= end) {
      this.calendarDays.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    const format = (d: Date) => d.toLocaleDateString('en-CA');
    this.searchParams.update(params => ({
      ...params,
      id: this.department_id() ?? -1,
      machine: this.selectedMachineId(),
      start_date: format(this.calendarDays[0]),
      end_date: format(this.calendarDays[this.calendarDays.length - 1])
    }));

    this.get();
  }

  downloadOperationSchedule(): void {
    if (!this.manualStartDate || !this.manualEndDate) {
      this.notification.set({ type: 'error', message: 'Please select both start and end dates' });
      return;
    }
    const machineId = this.selectedMachineId();
    if (!machineId) {
      this.notification.set({ type: 'error', message: 'Please select a machine first' });
      return;
    }

    const start_date = `${this.manualStartDate} 00:00:00`;
    const end_date = `${this.manualEndDate} 23:59:59`;

    this.loading.set(true);
    this.machineService.getOperationSchedule(machineId, { start_date, end_date }).subscribe({
      next: (response) => {
        this.loading.set(false);
        const data = response.data;
        if (!data || data.length === 0) {
            this.notification.set({ type: 'error', message: 'No data to download' });
            return;
        }

        if (typeof data === 'string' && (data.startsWith('http') || data.startsWith('/'))) {
            window.open(data, '_blank');
            return;
        }

        const columnMapping: { [key: string]: string } = {
          jobNo: 'Job No',
          requestDate: 'Customer Require Date',
          customer: 'Customer',
          jobDescription: 'Job Description',
          jobQuantity: 'Order Qty',
          nofImpressions: 'No.of Imp.',
          finishing: 'Finishing',
          estimatedDateTime: 'Printing Date',
          deliveryDate: 'CS Delivery Date',
          remarks: 'Remarks',
          colorReference: 'Color Reference',
          jobPassNumber: 'Job Pass Number'
        };
        const excludeColumns = ['id', 'jobOperationId', 'jobStatus'];
        
        let rawHeaders = Object.keys(data[0]).filter(h => !excludeColumns.includes(h));
        
        // Remove them so they can be explicitly placed
        rawHeaders = rawHeaders.filter(h => h !== 'remarks' && h !== 'jobPassNumber');
        
        // Add at beginning and end if they exist in the data
        if (Object.keys(data[0]).includes('remarks')) {
            rawHeaders.unshift('remarks');
        }
        
        if (rawHeaders[0] === 'remarks') {
            rawHeaders.splice(1, 0, 'colorReference');
        } else {
            rawHeaders.unshift('colorReference');
        }

        if (Object.keys(data[0]).includes('jobPassNumber')) {
            rawHeaders.push('jobPassNumber');
        }

        const displayHeaders = rawHeaders.map(h => columnMapping[h] || h);

        const formatDateForCSV = (dateStr: string) => {
          if (!dateStr) return '';
          const d = new Date(dateStr);
          if (isNaN(d.getTime())) return dateStr;
          const day = String(d.getDate()).padStart(2, '0');
          const month = d.toLocaleString('en-US', { month: 'short' });
          const year = d.getFullYear();
          return `${day}-${month}-${year}`;
        };

        const csvRows = [
            displayHeaders.join(','),
            ...data.map((row: any) => rawHeaders.map(field => {
                let value = row[field as keyof typeof row];
                if (value === null || value === undefined || String(value).trim().toLowerCase() === 'null') {
                    return '';
                }
                
                if (field === 'estimatedDateTime' || field === 'deliveryDate') {
                    value = formatDateForCSV(String(value));
                }
                
                return `"${String(value).replace(/"/g, '""')}"`;
            }).join(','))
        ];

        const csvContent = csvRows.join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');

        a.download = `operation_schedule_${this.manualStartDate}_to_${this.manualEndDate}.csv`;
        a.href = url;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.loading.set(false);
        this.notification.set({ type: 'error', message: 'Failed to download schedule' });
        console.error(err);
      }
    });
  }

  onManualStartDateChange(): void {
    if (this.manualStartDate) {
      const start = new Date(this.manualStartDate);
      const newEnd = new Date(start);
      newEnd.setDate(start.getDate() + 6);
      this.manualEndDate = this.formatDate(newEnd);
    }
  }

  onManualEndDateChange(): void {
    if (this.manualEndDate) {
      const end = new Date(this.manualEndDate);
      if (this.manualStartDate) {
        const start = new Date(this.manualStartDate);
        const diffTime = end.getTime() - start.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 6 || diffDays < 0) {
          const newStart = new Date(end);
          newStart.setDate(end.getDate() - 6);
          this.manualStartDate = this.formatDate(newStart);
        }
      } else {
        const newStart = new Date(end);
        newStart.setDate(end.getDate() - 6);
        this.manualStartDate = this.formatDate(newStart);
      }
    }
  }

  previousWeek(): void {
    if (this.startDayIndex() > 0) {
      this.startDayIndex.set(this.startDayIndex() - 7);

      this.generateCalendar();
      this.updateDateRange();
      this.get();
    }
  }

  // nextWeek(): void {
  //   const daysInMonth = new Date(this.selectedYear(), this.selectedMonth() + 1, 0).getDate();

  //   if (this.startDayIndex() + 7 < daysInMonth) {
  //     this.startDayIndex.set(this.startDayIndex() + 7);

  //     this.generateCalendar();
  //     this.updateDateRange();
  //     this.get();
  //   }
  // }

  nextWeek(): void {
    const daysInMonth = new Date(this.selectedYear(), this.selectedMonth() + 1, 0).getDate();
    if (this.startDayIndex() + 7 < daysInMonth) {
      this.startDayIndex.set(this.startDayIndex() + 7);
      this.generateCalendar();   // 1. sets calendarDays
      this.updateDateRange();    // 2. reads from calendarDays
      this.get();                // 3. uses updated searchParams
    }
  }

  get daysInCurrentMonth(): number {
    return new Date(this.selectedYear(), this.selectedMonth() + 1, 0).getDate();
  }

  updateCalendarYear(year: number): void {
    this.selectedYear.set(year);
    this.startDayIndex.set(0);

    this.generateCalendar();
    this.updateDateRange();
    this.get();
  }

  updateCalendarMonth(month: number): void {
    this.selectedMonth.set(month);
    this.startDayIndex.set(0);

    this.generateCalendar();
    this.updateDateRange();
    this.get();
  }

  generateTimeSlots(): void {
    this.timeSlots = Array.from({ length: 24 }, (_, i) => `${this.pad(i)}:00`);
  }

  pad(n: number): string {
    return n < 10 ? '0' + n : n.toString();
  }

  isSameDate(dateString: string, day: Date): boolean {
    if (!dateString) return false;
    const date = new Date(dateString);
    return (
      date.getFullYear() === day.getFullYear() &&
      date.getMonth() === day.getMonth() &&
      date.getDate() === day.getDate()
    );
  }

  public getRelevantDate(operation: any): string {
    return (
      operation.completedDateTime ||
      operation.startedDateTime ||
      operation.estimatedDateTime ||
      ''
    );
  }

  public get(): void {
    this.loading.set(true)
    this.departmentService.getOperationsByDep(this.searchParams()).pipe(
      tap(response => {
        this.loading.set(false);
        this.Department.set([response.data]);
        //  console.log(this.Department())
      }),
      catchError(error => {
        this.loading.set(false);
        this.notification.set({ type: 'error', message: `Failed to fetch Job Operation data` });
        console.error(error);
        return of(null);
      })
    ).subscribe();
  }
  public fetchDepartment(): void {
    this.departmentService.getAllDepartments().subscribe({
      next: (result) => {
        if (result.data && result.data.departments) {
          this.$$departmentDTO.set(result.data.departments);

          if (this.department_id()) {
            this.onDepartmentChange(this.department_id()!);
          }
        }
      },
      error: (error) => {
        console.error('Error fetching departments:', error);
      }
    });
  }

  $$filteredMachines = computed(() => {
    const selectedId = this.department_id();
    const departments = this.$$departmentDTO();

    if (selectedId === null) return [];

    const department = departments.find(d => d.departmentId === selectedId);
    return department?.machines ?? [];
  });

  selectedMachineName = computed(() => {
    const machines = this.$$filteredMachines();
    const id = this.selectedMachineId();
    return machines.find(m => m.machineId === id)?.machineName ?? null;
  });

  onDepartmentChange(departmentId: number): void {
    this.globalDepartmentService.setDepartmentId(departmentId);

    const department = this.$$departmentDTO().find(d => d.departmentId === departmentId);
    const firstMachine = department?.machines?.[0];
    const firstMachineId = firstMachine?.machineId ?? null;

    this.selectedMachineId.set(firstMachineId);

    this.searchParams.update(params => ({
      ...params,
      id: departmentId,
      machine: firstMachineId ?? null
    }));
    this.get();
  }

  getDuration(startedDateTime: string | null, completedDateTime: string | null): string {
    if (!startedDateTime || !completedDateTime) {
      return '-';
    }

    const startDate = new Date(startedDateTime);
    const endDate = new Date(completedDateTime);

    const durationMs = endDate.getTime() - startDate.getTime();
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours} hours ${minutes} minutes`;
    } else if (minutes > 0) {
      return `${minutes} minutes ${seconds} seconds`;
    } else {
      return `${seconds} seconds`;
    }
  }

  get canChangeStatus(): boolean {
    return this.authService.hasContextPermission('machine-timeline', 'reschedule');
  }

  get canManageService(): boolean {
    return this.authService.hasContextPermission('machine-timeline', 'service');
  }

  protected onOperationOpenModal(ID: number, prefillDate?: string): void {

    const canChangeStatus = this.authService.hasContextPermission('machine-timeline', 'reschedule');
    this.showOperationReschedule.set(canChangeStatus);

    this.getAllMachines();
    this.modal.$$openOpeartion.set(true);
    this.rescheduledHoldReason.set(null);
    this.jobOperationService.getById(String(ID)).pipe(
      tap(response => {
        this.loading.set(false);
        this.GetJobOperationDTO.set([response.data]);
        this.selectedPlannedQuantity = this.GetJobOperationDTO()[0].quantity.toString();
        this.machineId = this.GetJobOperationDTO()[0].machine_details.id;
        
        if (prefillDate) {
          this.newEstimateDateTime = prefillDate;
        } else if (response.data && response.data.estimatedDateTime) {
          this.newEstimateDateTime = this.formatToLocalDatetime(response.data.estimatedDateTime);
        } else {
          this.newEstimateDateTime = this.minDateTime;
        }
        
        this.filterOperationsByDate(this.newEstimateDateTime);
        this.estimateDateTime = this.formatToLocalDatetime(this.GetJobOperationDTO()[0].estimatedDateTime);
        
        if (response.data.prevOperationId) {
          this.jobOperationService.getRescheduledHoldReason(response.data.id).pipe(
            tap(res => {
              if (res.data) {
                this.rescheduledHoldReason.set(res.data);
              }
            })
          ).subscribe();
        }
        // this.fetchMachineOperations(this.machineId);
        // console.log('neww',this.GetJobOperationDTO()[0].estimatedDateTime)
      }),
      catchError(error => {
        this.loading.set(false);
        this.notification.set({ type: 'error', message: `Failed to load Job Operation details` });
        console.error(error);
        return of(null);
      })
    ).subscribe();
  }

  // fetchMachineOperations(machineId: number): void {
  //   const department = this.Department();
  //   console.log(this.newEstimateDateTime, machineId);


  // }

  filterOperationsByDate(selectedDate: string): void {
    if (!selectedDate) {
      this.FilteredOperations.set([]);
      return;
    }

    this.searchParamsForMachine.update(params => ({
      ...params,
      id: this.department_id() ?? -1, //add -1
      machine: this.machineId,
      start_date: this.formatDate(this.newEstimateDateTime),
      end_date: this.formatDate(this.newEstimateDateTime)
    }));

    this.departmentService.getOperationsByDep(this.searchParamsForMachine()).pipe(
      tap(response => {
        this.loading.set(false);
        this.DepartmentNew.set([response.data]);
        this.FilteredOperations.set(this.DepartmentNew()[0].machines[0].operations)
        //  console.log(this.Department())
      }),
      catchError(error => {
        this.loading.set(false);
        this.notification.set({ type: 'error', message: `Failed to fetch Job Operation data` });
        console.error(error);
        return of(null);
      })
    ).subscribe();
  }

  selectTimeSlot(estimatedDateTime: string, estimateTime: number): void {
    const endTime: Date = this.getEndTime(estimatedDateTime, estimateTime);
    this.newEstimateDateTime = this.formatToLocalDatetime(endTime.toISOString());
    this.filterOperationsByDate(this.newEstimateDateTime);
  }


  getEndTime(startDateTime: string, estimateTime: number): Date {
    const start = new Date(startDateTime);
    return new Date(start.getTime() + estimateTime * 60 * 60 * 1000);
  }

  isOperationOnSelectedDate(operation: any, selectedDate: string): boolean {
    if (!operation.estimatedDateTime || !selectedDate) return false;

    const operationDateStr = operation.estimatedDateTime.substring(0, 10);
    const selectedDateStr = selectedDate.substring(0, 10);

    return operationDateStr === selectedDateStr;
  }

  onDateChange(): void {
    this.filterOperationsByDate(this.newEstimateDateTime);
  }

  formatToLocalDatetime(utcString: string): string {
    const date = new Date(utcString);
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60000);
    return localDate.toISOString().slice(0, 16);
  }

  getAllMachines() {
    this.machineService.getAll().subscribe({
      next: (result) => {
        if (result.data) {
          this.$$MachineDTO.set(result.data);
        }
      },
      error: (error) => {
        console.error('Error fetching machines:', error);
      }
    });
  }


  hourIndex(hour: string): number {
    return parseInt(hour.split(':')[0], 10);
  }

  isOperationInHour(operation: any, day: Date, hour: string): boolean {
    if (!operation.estimatedDateTime || !operation.estimateTime) return false;

    const startDate = new Date(operation.estimatedDateTime);
    const durationHours = operation.estimateTime || 1; // Default to 1 hour if missing
    const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000);

    const hourNum = parseInt(hour.split(':')[0], 10);
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hourNum);
    const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hourNum + 1);

    // Check if the operation overlaps with the current hour slot
    return startDate < dayEnd && endDate > dayStart;
  }

  getOperationTop(operation: any, day: Date, hour: string): number {
    if (!operation.estimatedDateTime) return -48;

    const startDate = new Date(operation.estimatedDateTime);
    const hourNum = parseInt(hour.split(':')[0], 10);
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hourNum);

    // If the operation starts after the current hour, hide it
    if (startDate >= new Date(dayStart.getTime() + 60 * 60 * 1000)) {
      return -48;
    }

    // If the operation starts before the current day, adjust top to start of the hour
    if (startDate < dayStart) {
      return 0;
    }

    const minutes = startDate.getMinutes();
    const pixelsPerMinute = 48 / 60; // 48px per hour / 60 minutes
    return minutes * pixelsPerMinute; // Offset within the hour slot
  }

  getOperationHeight(operation: any, day: Date): number {
    if (!operation.estimatedDateTime || !operation.estimateTime) return 48;

    const startDate = new Date(operation.estimatedDateTime);
    const durationHours = operation.estimateTime || 1; // Default to 1 hour
    const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000);

    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);

    // Calculate the overlap duration within the current day
    const overlapStart = startDate > dayStart ? startDate : dayStart;
    const overlapEnd = endDate < dayEnd ? endDate : dayEnd;
    const overlapMs = overlapEnd.getTime() - overlapStart.getTime();

    if (overlapMs <= 0) return 0; // No overlap

    const durationMinutes = overlapMs / (1000 * 60); // Convert to minutes
    const pixelsPerMinute = 48 / 60; // 48px per hour / 60 minutes
    return durationMinutes * pixelsPerMinute; // Height in pixels
  }

  getOperationDayTop(operation: any, day: Date): number {
    if (!operation.estimatedDateTime) return 0;

    const startDate = new Date(operation.estimatedDateTime);
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());

    // If operation starts before the current day, top is 0
    if (startDate < dayStart) {
      return 0;
    }

    // Calculate top position based on start time within the day
    const hours = startDate.getHours();
    const minutes = startDate.getMinutes();
    const pixelsPerMinute = 48 / 60; // 48px per hour / 60 minutes
    return (hours * 60 + minutes) * pixelsPerMinute;
  }

  isOperationInDay(operation: any, day: Date): boolean {
    if (!operation.estimatedDateTime || !operation.estimateTime) return false;

    const startDate = new Date(operation.estimatedDateTime);
    const durationHours = operation.estimateTime || 1;
    const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000);

    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);

    return startDate < dayEnd && endDate > dayStart;
  }


  /**
   * Returns the free gap immediately after `currentOp` ends, before the next
   * operation on the same machine starts (or end-of-day if it is the last op).
   * Returns null when the gap is < 5 minutes.
   */
  public getFreeGapAfterOp(
    currentOp: any,
    allOps: any[],
    day: Date
  ): { startTime: string; endTime: string; durationHours: number; nextOpId?: number; fullStartTime?: string } | null {
    if (!currentOp?.estimatedDateTime || !currentOp?.estimateTime) return null;

    const dayEndMs = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1).getTime();
    const dayStartMs = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();

    const opsInDay = allOps
      .filter(op =>
        op.jobStatus !== 'maintenance cancelled' &&
        op.estimatedDateTime &&
        op.estimateTime &&
        this.isOperationInDay(op, day)
      )
      .sort((a, b) => new Date(a.estimatedDateTime).getTime() - new Date(b.estimatedDateTime).getTime());

    const idx = opsInDay.indexOf(currentOp);
    if (idx === -1) return null;

    const currentEndMs = Math.min(
      new Date(currentOp.estimatedDateTime).getTime() + currentOp.estimateTime * 3600000,
      dayEndMs
    );

    const nextOp = opsInDay[idx + 1];
    const nextStartMs = nextOp
      ? Math.max(dayStartMs, new Date(nextOp.estimatedDateTime).getTime())
      : dayEndMs;

    const gapMs = nextStartMs - currentEndMs;
    if (gapMs < 5 * 60 * 1000) return null;

    return {
      startTime: this.formatHHmm(new Date(currentEndMs)),
      endTime:   this.formatHHmm(new Date(nextStartMs)),
      durationHours: Number((gapMs / 3600000).toFixed(2)),
      nextOpId: nextOp ? (nextOp.operationId ?? nextOp.id) : undefined,
      fullStartTime: nextOp ? this.formatToLocalDatetime(new Date(currentEndMs).toISOString()).replace('T', ' ') : undefined
    };
  }

  public getFreeGapAtStart(
    allOps: any[],
    day: Date
  ): { startTime: string; endTime: string; durationHours: number; nextOpId?: number; fullStartTime?: string } | null {
    if (!allOps || allOps.length === 0) return null;

    const dayStartMs = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();

    const opsInDay = allOps
      .filter(op =>
        op.jobStatus !== 'maintenance cancelled' &&
        op.estimatedDateTime &&
        op.estimateTime &&
        this.isOperationInDay(op, day)
      )
      .sort((a, b) => new Date(a.estimatedDateTime).getTime() - new Date(b.estimatedDateTime).getTime());

    if (opsInDay.length === 0) return null;

    if (this.hasContinuedJobAtStart(allOps, day)) return null;

    const firstOp = opsInDay[0];
    const firstStartMs = Math.max(dayStartMs, new Date(firstOp.estimatedDateTime).getTime());

    const gapMs = firstStartMs - dayStartMs;
    if (gapMs < 5 * 60 * 1000) return null;

    return {
      startTime: this.formatHHmm(new Date(dayStartMs)),
      endTime:   this.formatHHmm(new Date(firstStartMs)),
      durationHours: Number((gapMs / 3600000).toFixed(2)),
      nextOpId: firstOp.operationId ?? firstOp.id,
      fullStartTime: this.formatToLocalDatetime(new Date(dayStartMs).toISOString()).replace('T', ' ')
    };
  }
  isContinuedFromPreviousDay(operation: any, currentDay: Date): boolean {
    if (!operation.estimatedDateTime) return false;
    const startDate = new Date(operation.estimatedDateTime);
    const dayStart = new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate());
    return startDate < dayStart;
  }

  hasContinuedJobAtStart(operations: any[], currentDay: Date): boolean {
    return operations.some(op => this.isOperationInDay(op, currentDay) && this.isContinuedFromPreviousDay(op, currentDay));
  }

  isContinuingToNextDay(operation: any, currentDay: Date): boolean {
    if (!operation.estimatedDateTime || !operation.estimateTime) return false;
    const startDate = new Date(operation.estimatedDateTime);
    const durationHours = operation.estimateTime || 1;
    const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000);
    const dayEnd = new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate() + 1);
    return endDate > dayEnd;
  }

  removeGap(opId: number, fullStartTime: string) {
    this.loading.set(true);
    this.jobOperationService.removeGaps(opId, fullStartTime).subscribe({
      next: () => {
        this.get();
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error removing gap:', err);
        this.loading.set(false);
      }
    });
  }

  moveOperation(id: number) {

    this.loading.set(true);

    const dto: RescheduleJobOperationDTO = {
      machineId: this.machineId!,
      jobCardId: this.GetJobOperationDTO()[0].job_details.id,
      noOfSheets: this.GetJobOperationDTO()[0].noOfSheets,
      jobStatus: this.GetJobOperationDTO()[0].jobStatus,
      estimateTime: this.GetJobOperationDTO()[0].estimateTime,
      estimateDateTime: this.estimateDateTime.replace('T', ' '),
      rescheduledEstimateDateTime: this.newEstimateDateTime.replace('T', ' '),
      plannedQuantity: Number(this.selectedPlannedQuantity ?? this.GetJobOperationDTO()[0].quantity)
    };


    this.jobOperationService.updateJobOperation(id, dto).subscribe({
      next: (res) => {
        this.get();
        this.modal.$$openOpeartion.set(false);
        this.loading.set(false);
        this.notification.set({ type: 'success', message: `Job Operation Moved Successfully!` });
      },
      error: (err) => {
        console.error('Failed to move operation:', err);
        this.loading.set(false);
        this.notification.set({ type: 'error', message: `Failed to Move Job Operation!` });
      }
    });
  }

  moveMachine(id: number) {
    const currentOp = this.GetJobOperationDTO()[0];
    const dto: MachineRescheduleDTO = {
      machineId: this.machineId!,
      estimateTime: currentOp.estimateTime,
      estimateDateTime: this.formatToLocalDatetime(currentOp.estimatedDateTime).replace('T', ' '),
      rescheduledEstimateDateTime: this.machineChangeEstimateDateTime.replace('T', ' '),
    };

    this.loading.set(true);
    this.jobOperationService.machineReschedule(id, dto).subscribe({
      next: (res) => {
        this.get();
        this.modal.$$openOpeartion.set(false);
        this.loading.set(false);
        this.notification.set({ type: 'success', message: `Machine Changed Successfully!` });
      },
      error: (err) => {
        this.loading.set(false);
        this.notification.set({ type: 'error', message: `Failed to Change Machine!` });
      }
    });
  }


  isTodayOrTomorrow(dateString: string): boolean {
    if (!dateString) return false;

    const inputDate = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    const inputDay = inputDate.toDateString();
    return inputDay === today.toDateString() || inputDay === tomorrow.toDateString();
  }

  isToday(date: Date | string): boolean {
    if (!date) return false;
    const inputDate = new Date(date);
    const today = new Date();
    return inputDate.toDateString() === today.toDateString();
  }

  formatNewDateTime(dateStr: string | null): string {
    if (!dateStr) return '-';
    return dateStr.slice(0, 16).replace('T', ' ');
  }

  getPlannedQuantityForDay(operation: any, day: Date): number {
    if (!operation.estimateTime || !operation.operationQuantity) return operation.operationQuantity || 0;
    const hoursInDay = this.getOperationHeight(operation, day) / 48;
    const ratio = hoursInDay / operation.estimateTime;
    return Math.round(operation.operationQuantity * ratio);
  }

  getTotalQuantityForDay(day: Date): number {
    let total = 0;
    const deps = this.Department();
    if (!deps) return total;
    for (const dep of deps) {
      if (dep.machines) {
        for (const machine of dep.machines) {
          if (machine.operations) {
            for (const op of machine.operations) {
              if (this.isOperationInDay(op, day) && op.jobStatus !== 'maintenance cancelled') {
                total += this.getPlannedQuantityForDay(op, day);
              }
            }
          }
        }
      }
    }
    return total;
  }

  getPlannedJobCountForDay(day: Date): number {
    let count = 0;
    const deps = this.Department();
    if (!deps) return count;
    for (const dep of deps) {
      if (dep.machines) {
        for (const machine of dep.machines) {
          if (machine.operations) {
            for (const op of machine.operations) {
              if (this.isOperationInDay(op, day) && op.jobStatus && !op.jobStatus.includes('maintenance')) {
                count++;
              }
            }
          }
        }
      }
    }
    return count;
  }

  // --- Drag and Drop Methods ---  draggedOperation: any = null;
  draggedOperationMachineId: number | null = null;




  toggleEditMode(): void {
    this.isEditMode.set(!this.isEditMode());
  }

  onDragStart(event: DragEvent, operation: any, machine: any): void {
    if (!this.isEditMode()) {
      event.preventDefault();
      return;
    }
    this.draggedOperation = operation;
    this.draggedOperationMachineId = machine?.machineId || null;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', operation.operationId.toString());
    }
  }

  allowDrop(event: DragEvent, dropTargetDate?: Date): void {
    if (this.isEditMode()) {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
    }
  }

  onDropToDateHeader(event: DragEvent, day: Date): void {
    event.preventDefault();
    if (!this.isEditMode() || !this.draggedOperation || !this.draggedOperationMachineId) return;

    const dropDate = new Date(day);
    dropDate.setHours(0, 0, 0, 0);

    const formattedDate = this.formatToLocalDatetimeForDrop(dropDate);
    this.autoRescheduleOperation(this.draggedOperation, formattedDate, this.draggedOperationMachineId);
    this.draggedOperation = null;
    this.draggedOperationMachineId = null;
  }

  onDropToColumnTop(event: DragEvent, day: Date, targetMachine: any): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isEditMode() || !this.draggedOperation) return;

    const dropDate = new Date(day);
    dropDate.setHours(0, 0, 0, 0);

    const formattedDate = this.formatToLocalDatetimeForDrop(dropDate);
    this.autoRescheduleOperation(this.draggedOperation, formattedDate, targetMachine.machineId);
    this.draggedOperation = null;
  }

  getLastOperationForDay(targetMachine: any, day: Date): any {
    if (!targetMachine || !targetMachine.operations) return null;
    
    const operationsForDay = targetMachine.operations.filter((op: any) => 
      this.isOperationInDay(op, day) && op.jobStatus !== 'maintenance cancelled'
    );
    
    if (operationsForDay.length === 0) return null;
    
    operationsForDay.sort((a: any, b: any) => {
      const aEnd = this.calculateEndTime(a).getTime();
      const bEnd = this.calculateEndTime(b).getTime();
      return bEnd - aEnd;
    });
    
    return operationsForDay[0];
  }

  onDropToDay(event: DragEvent, day: Date, targetMachine: any): void {
    event.preventDefault();
    if (!this.isEditMode() || !this.draggedOperation) return;

    const lastOperation = this.getLastOperationForDay(targetMachine, day);

    if (lastOperation) {
      const endDate = this.calculateEndTime(lastOperation);
      const formattedDate = this.formatToLocalDatetimeForDrop(endDate);
      this.autoRescheduleOperation(this.draggedOperation, formattedDate, targetMachine.machineId);
    } else {
      const dropDate = new Date(day);
      dropDate.setHours(0, 0, 0, 0);
      const formattedDate = this.formatToLocalDatetimeForDrop(dropDate);
      this.autoRescheduleOperation(this.draggedOperation, formattedDate, targetMachine.machineId);
    }
    
    this.draggedOperation = null;
  }

  onDropToJob(event: DragEvent, targetOperation: any, day: Date, targetMachine: any): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isEditMode() || !this.draggedOperation) return;

    if (this.draggedOperation.operationId === targetOperation.operationId) {
      this.draggedOperation = null;
      return;
    }

    const endDate = this.calculateEndTime(targetOperation);
    const formattedDate = this.formatToLocalDatetimeForDrop(endDate);

    this.autoRescheduleOperation(this.draggedOperation, formattedDate, targetMachine.machineId);
    this.draggedOperation = null;
  }

  autoRescheduleOperation(operation: any, newEstimateDateTime: string, targetMachineId: number): void {
    this.loading.set(true);
    
    // Fetch full details required for the Reschedule DTO
    this.jobOperationService.getById(String(operation.operationId)).subscribe({
      next: (response) => {
        const fullOp = response.data;
        const originalEstimate = this.formatToLocalDatetime(fullOp.estimatedDateTime).replace('T', ' ');
        const rescheduledEstimate = newEstimateDateTime.replace('T', ' ');

        const dto: RescheduleJobOperationDTO = {
          machineId: targetMachineId,
          jobCardId: fullOp.job_details.id,
          noOfSheets: fullOp.noOfSheets,
          jobStatus: fullOp.jobStatus,
          estimateTime: fullOp.estimateTime,
          estimateDateTime: originalEstimate,
          rescheduledEstimateDateTime: rescheduledEstimate,
          plannedQuantity: fullOp.quantity
        };

        this.jobOperationService.updateJobOperation(operation.operationId, dto).subscribe({
          next: () => {
            this.get();
            this.loading.set(false);
            this.notification.set({ type: 'success', message: `Job Operation Rescheduled Successfully!` });
          },
          error: (err) => {
            console.error('Failed to reschedule operation:', err);
            this.loading.set(false);
            this.notification.set({ type: 'error', message: `Failed to Reschedule Job Operation!` });
          }
        });
      },
      error: (err) => {
        console.error('Failed to fetch job details for rescheduling:', err);
        this.loading.set(false);
        this.notification.set({ type: 'error', message: `Failed to fetch Job Operation details!` });
      }
    });
  }

  calculateEndTime(op: any): Date {
    const startStr = this.getRelevantDate(op);
    if (!startStr) return new Date();
    const startDate = new Date(startStr);
    const estimateTimeHrs = op.estimateTime || 0;
    startDate.setMinutes(startDate.getMinutes() + (estimateTimeHrs * 60));
    return startDate;
  }

  formatToLocalDatetimeForDrop(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }
  // -----------------------------

  getCapacityForDay(): number {
    let cap = 0;
    const deps = this.Department();
    if (!deps) return cap;
    for (const dep of deps) {
      if (dep.machines) {
        for (const machine of dep.machines) {
          cap += (machine.hourlyOutput || 0) * 24;
        }
      }
    }
    return cap;
  }

  getOvertimeSuggested(day: Date): string | null {
    let maxEndTime = 0;
    const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1).getTime();
    
    const deps = this.Department();
    if (!deps) return null;
    for (const dep of deps) {
      if (dep.machines) {
        for (const machine of dep.machines) {
          if (machine.operations) {
            for (const op of machine.operations) {
              if (op.estimatedDateTime && op.estimateTime && op.jobStatus !== 'maintenance cancelled') {
                const opStart = new Date(op.estimatedDateTime);
                if (opStart.getFullYear() === day.getFullYear() && opStart.getMonth() === day.getMonth() && opStart.getDate() === day.getDate()) {
                  const opEnd = opStart.getTime() + op.estimateTime * 60 * 60 * 1000;
                  if (opEnd > maxEndTime) {
                    maxEndTime = opEnd;
                  }
                }
              }
            }
          }
        }
      }
    }
    
    if (maxEndTime > dayEnd) {
      const overtimeHours = (maxEndTime - dayEnd) / (1000 * 60 * 60);
      return this.formatHours(overtimeHours);
    }
    
    return null;
  }

  getRemainingTimeForDay(day: Date): string | null {
    let totalOccupiedHours = 0;
    
    const deps = this.Department();
    if (!deps) return null;
    for (const dep of deps) {
      if (dep.machines) {
        for (const machine of dep.machines) {
          if (machine.operations) {
            for (const op of machine.operations) {
              if (this.isOperationInDay(op, day) && op.jobStatus !== 'maintenance cancelled') {
                const hoursInDay = this.getOperationHeight(op, day) / 48;
                totalOccupiedHours += hoursInDay;
              }
            }
          }
        }
      }
    }
    
    const remainingHours = 24 - totalOccupiedHours;
    if (remainingHours > 0 && remainingHours < 24) {
      return this.formatHours(remainingHours);
    } else if (remainingHours >= 24) {
      return '24 hours';
    }
    
    return null;
  }

  getLastReason(operation: any): string | null {
    if (operation.pausedReasons && operation.pausedReasons.length > 0) {
      return operation.pausedReasons[operation.pausedReasons.length - 1].reason;
    }
    return null;
  }

  scrollToMachine(machineId: number): void {
    this.selectedMachineId.set(machineId);
    console.log(machineId);
    this.searchParams.update(params => ({
      ...params,
      machine: machineId
    }));
    this.get();
  }

  formatHours(value: number): string {
    if (!value || isNaN(value)) return '0 hours';

    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 60);

    const hourText = hours > 0 ? `${hours} hour${hours > 1 ? 's' : ''}` : '';
    const minuteText = minutes > 0 ? `${minutes} min${minutes > 1 ? 's' : ''}` : '';

    return [hourText, minuteText].filter(Boolean).join(' ');
  }

  onMachineSelect(machineId: number) {
    this.MachineChangeFilteredOperations.set([]);
    this.machineService.getNextAvailableTime(machineId).pipe(
      tap(response => {
        const nextAvailableTime = response.data.nextAvailableTime;
        this.machineChangeEstimateDateTime = this.formatNewDateTime(nextAvailableTime);
        console.log(nextAvailableTime);
        this.filterOperationsByMachineChange();
      }),
      catchError(error => {
        this.notification.set({ type: 'error', message: `Failed to get next available time` });
        console.error(error);
        return of(null);
      })
    ).subscribe();
  }

  onMachineChangeDateChange(): void {
    this.filterOperationsByMachineChange();
  }

  selectFreeTimeSlot(startFullDateTime: string): void {
    this.newEstimateDateTime = startFullDateTime;
    this.filterOperationsByDate(this.newEstimateDateTime);
  }

  selectMachineChangeFreeTimeSlot(startFullDateTime: string): void {
    this.machineChangeEstimateDateTime = startFullDateTime;
    this.filterOperationsByMachineChange();
  }

  selectMachineChangeTimeSlot(estimatedDateTime: string, estimateTime: number): void {
    const endTime: Date = this.getEndTime(estimatedDateTime, estimateTime);
    this.machineChangeEstimateDateTime = this.formatToLocalDatetime(endTime.toISOString());
    this.filterOperationsByMachineChange();
  }

  selectMaintenanceFreeTimeSlot(startFullDateTime: string): void {
    this.maintenanceEstimatedDateTime = startFullDateTime;
    this.filterOperationsByMaintenance();
  }

  selectMaintenanceTimeSlot(estimatedDateTime: string, estimateTime: number): void {
    const endTime: Date = this.getEndTime(estimatedDateTime, estimateTime);
    this.maintenanceEstimatedDateTime = this.formatToLocalDatetime(endTime.toISOString());
    this.filterOperationsByMaintenance();
  }

  getFreeTimeSlots(operations: any[], selectedDateTimeStr: string): { startTime: string; endTime: string; durationHours: number; startFullDateTime: string }[] {
    if (!selectedDateTimeStr) return [];

    const dateOnly = selectedDateTimeStr.substring(0, 10);
    if (!dateOnly || dateOnly.length < 10) return [];

    const dayStart = new Date(`${dateOnly}T00:00:00`);
    const dayEnd = new Date(`${dateOnly}T23:59:59`);
    const dayStartMs = dayStart.getTime();
    const dayEndMs = dayEnd.getTime() + 1000;

    const validOps = (operations || []).filter(op => op.jobStatus !== 'maintenance cancelled' && op.estimatedDateTime && op.estimateTime);

    const intervals: { start: number; end: number }[] = [];
    for (const op of validOps) {
      const start = new Date(op.estimatedDateTime).getTime();
      const end = start + (op.estimateTime || 0) * 60 * 60 * 1000;

      const clampedStart = Math.max(dayStartMs, start);
      const clampedEnd = Math.min(dayEndMs, end);

      if (clampedStart < clampedEnd) {
        intervals.push({ start: clampedStart, end: clampedEnd });
      }
    }

    intervals.sort((a, b) => a.start - b.start);

    const merged: { start: number; end: number }[] = [];
    for (const item of intervals) {
      if (merged.length === 0) {
        merged.push({ ...item });
      } else {
        const last = merged[merged.length - 1];
        if (item.start <= last.end) {
          last.end = Math.max(last.end, item.end);
        } else {
          merged.push({ ...item });
        }
      }
    }

    const freeSlots: { startTime: string; endTime: string; durationHours: number; startFullDateTime: string }[] = [];
    let currentStart = dayStartMs;

    for (const opInterval of merged) {
      if (opInterval.start > currentStart) {
        const gapMs = opInterval.start - currentStart;
        const durationHours = Number((gapMs / (60 * 60 * 1000)).toFixed(2));
        if (durationHours >= 0.08) {
          const startDate = new Date(currentStart);
          const endDate = new Date(opInterval.start);
          freeSlots.push({
            startTime: this.formatHHmm(startDate),
            endTime: this.formatHHmm(endDate),
            durationHours,
            startFullDateTime: this.formatToLocalDatetime(startDate.toISOString())
          });
        }
      }
      currentStart = Math.max(currentStart, opInterval.end);
    }

    if (currentStart < dayEndMs) {
      const gapMs = dayEndMs - currentStart;
      const durationHours = Number((gapMs / (60 * 60 * 1000)).toFixed(2));
      if (durationHours >= 0.08) {
        const startDate = new Date(currentStart);
        const endDate = new Date(dayEndMs);
        freeSlots.push({
          startTime: this.formatHHmm(startDate),
          endTime: this.formatHHmm(endDate),
          durationHours,
          startFullDateTime: this.formatToLocalDatetime(startDate.toISOString())
        });
      }
    }

    return freeSlots;
  }

  private formatHHmm(date: Date): string {
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  /**
   * Returns a chronologically-sorted timeline that interleaves free slots and
   * occupied operations so they can be displayed in a single unified list.
   */
  public getMergedTimeline(
    operations: any[],
    selectedDateTimeStr: string
  ): TimelineItem[] {
    if (!selectedDateTimeStr) return [];

    const dateOnly = selectedDateTimeStr.substring(0, 10);
    if (!dateOnly || dateOnly.length < 10) return [];

    const dayStart = new Date(`${dateOnly}T00:00:00`);
    const dayEnd   = new Date(`${dateOnly}T23:59:59`);
    const dayStartMs = dayStart.getTime();
    const dayEndMs   = dayEnd.getTime() + 1000;

    // ── occupied items ──────────────────────────────────────────────────────
    const validOps = (operations || []).filter(
      op => op.jobStatus !== 'maintenance cancelled' && op.estimatedDateTime && op.estimateTime
    );

    const occupiedItems = validOps.map(op => {
      const startMs = new Date(op.estimatedDateTime).getTime();
      const endMs   = startMs + op.estimateTime * 60 * 60 * 1000;
      const clampedStart = Math.max(dayStartMs, startMs);
      const clampedEnd   = Math.min(dayEndMs, endMs);
      return {
        type: 'occupied' as const,
        startTime: this.formatHHmm(new Date(clampedStart)),
        endTime:   this.formatHHmm(new Date(clampedEnd)),
        startMs:   clampedStart,
        durationHours: Number(((clampedEnd - clampedStart) / (60 * 60 * 1000)).toFixed(2)),
        startFullDateTime: this.formatToLocalDatetime(new Date(clampedStart).toISOString()),
        jobNo:           op.jobNo,
        jobStatus:       op.jobStatus,
        estimatedDateTime: op.estimatedDateTime,
        estimateTime:    op.estimateTime,
      };
    }).filter(item => item.startMs < dayEndMs && item.durationHours > 0);

    // ── free items ──────────────────────────────────────────────────────────
    const freeSlots = this.getFreeTimeSlots(operations, selectedDateTimeStr);
    const freeItems = freeSlots.map(slot => ({
      type: 'free' as const,
      startTime: slot.startTime,
      endTime:   slot.endTime,
      startMs:   new Date(`${dateOnly}T${slot.startTime}:00`).getTime(),
      durationHours: slot.durationHours,
      startFullDateTime: slot.startFullDateTime,
    }));

    // ── merge and sort ───────────────────────────────────────────────────────
    const merged = [...occupiedItems, ...freeItems];
    merged.sort((a, b) => a.startMs - b.startMs);
    return merged;
  }

  filterOperationsByMachineChange(): void {
    if (!this.machineId || !this.machineChangeEstimateDateTime) {
      this.MachineChangeFilteredOperations.set([]);
      return;
    }

    const dateStr = this.formatDate(this.machineChangeEstimateDateTime);
    const params = {
      id: this.department_id() ?? -1,
      machine: this.machineId,
      start_date: dateStr,
      end_date: dateStr
    };

    this.departmentService.getOperationsByDep(params).pipe(
      tap(response => {
        const machines = response.data?.machines;
        if (machines && machines.length > 0) {
          this.MachineChangeFilteredOperations.set(machines[0].operations ?? []);
        } else {
          this.MachineChangeFilteredOperations.set([]);
        }
      }),
      catchError(error => {
        console.error(error);
        this.MachineChangeFilteredOperations.set([]);
        return of(null);
      })
    ).subscribe();
  }

  filterOperationsByMaintenance(): void {
    if (!this.maintenanceMachineId || !this.maintenanceEstimatedDateTime) {
      this.MaintenanceFilteredOperations.set([]);
      return;
    }

    const dateStr = this.formatDate(this.maintenanceEstimatedDateTime);
    const params = {
      id: this.department_id() ?? -1,
      machine: this.maintenanceMachineId,
      start_date: dateStr,
      end_date: dateStr
    };

    this.departmentService.getOperationsByDep(params).pipe(
      tap(response => {
        const machines = response.data?.machines;
        if (machines && machines.length > 0) {
          this.MaintenanceFilteredOperations.set(machines[0].operations ?? []);
        } else {
          this.MaintenanceFilteredOperations.set([]);
        }
      }),
      catchError(error => {
        console.error(error);
        this.MaintenanceFilteredOperations.set([]);
        return of(null);
      })
    ).subscribe();
  }

  onMaintenanceDateChange(): void {
    this.filterOperationsByMaintenance();
  }

  updateDateRange(): void {
  if (this.calendarDays.length === 0) return;

  const format = (d: Date) => d.toLocaleDateString('en-CA');

  // Derive directly from the rendered calendar days — single source of truth
  const startDate = this.calendarDays[0];
  const endDate = this.calendarDays[this.calendarDays.length - 1];

  this.start_date = format(startDate);
  this.end_date = format(endDate);

  this.searchParams.update(params => ({
    ...params,
    start_date: this.start_date,
    end_date: this.end_date
  }));
}

  openPendingJobsModal(): void {
    this.modal.$$pendingJobs.set(true);
  }

  openAddServicesModal(): void {
    if (this.$$departmentDTO().length > 0) {
       this.maintenanceMachineId = this.selectedMachineId();
    } else {
       this.maintenanceMachineId = null;
    }
    this.maintenanceEstimatedDateTime = '';
    this.maintenanceEstimateTime = null;
    this.maintenanceRemarks = '';
    this.modal.$$addServices.set(true);
  }

  submitMaintenance(): void {
    if (!this.maintenanceMachineId || !this.maintenanceEstimatedDateTime || !this.maintenanceEstimateTime) {
      this.notification.set({ type: 'error', message: 'Please fill all required fields' });
      return;
    }

    let formattedDateTime = this.maintenanceEstimatedDateTime.replace('T', ' ');
    if (formattedDateTime.length === 16) {
      formattedDateTime += ':00';
    }

    const payload = {
      machineId: this.maintenanceMachineId,
      estimatedDateTime: formattedDateTime,
      estimateTime: this.maintenanceEstimateTime,
      remarks: this.maintenanceRemarks
    };

    this.loading.set(true);
    this.jobOperationService.addMaintenance(payload).subscribe({
      next: (res) => {
        this.get();
        this.modal.$$addServices.set(false);
        this.loading.set(false);
        this.notification.set({ type: 'success', message: 'Maintenance added successfully!' });
      },
      error: (err) => {
        console.error('Failed to add maintenance:', err);
        this.loading.set(false);
        this.notification.set({ type: 'error', message: 'Failed to add maintenance!' });
      }
    });
  }

  openCancelMaintenanceModal(id: number): void {
    this.cancelMaintenanceId = id;
    this.cancelMaintenanceRemarks = '';
    this.modal.$$cancelMaintenance.set(true);
  }

  submitCancelMaintenance(): void {
    if (!this.cancelMaintenanceId || !this.cancelMaintenanceRemarks.trim()) {
      this.notification.set({ type: 'error', message: 'Remarks are required for cancellation' });
      return;
    }

    this.loading.set(true);
    this.jobOperationService.cancelMaintenance(this.cancelMaintenanceId, { remarks: this.cancelMaintenanceRemarks }).subscribe({
      next: () => {
        this.get();
        this.modal.$$cancelMaintenance.set(false);
        this.modal.$$openOpeartion.set(false);
        this.loading.set(false);
        this.notification.set({ type: 'success', message: 'Maintenance canceled successfully!' });
      },
      error: (err) => {
        console.error('Failed to cancel maintenance:', err);
        this.loading.set(false);
        this.notification.set({ type: 'error', message: 'Failed to cancel maintenance!' });
      }
    });
  }

  onCardClick(event: MouseEvent, operation: any): void {
    if (this.isEditMode()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (operation.jobStatus === 'maintenance scheduled') {
      if (this.canManageService) {
        this.openCancelMaintenanceModal(operation.operationId ?? operation.id);
      }
    } else if (operation.jobStatus === 'maintenance ongoing' || operation.jobStatus === 'maintenance completed' || operation.jobStatus === 'maintenance cancelled') {
      // Do nothing for ongoing, completed, and cancelled maintenance
    } else {
      this.onOperationOpenModal(operation.operationId ?? operation.id);
    }
  }

  closeOperationModal(): void {
    this.machineId = null;
    this.machineChangeEstimateDateTime = '';
    this.MachineChangeFilteredOperations.set([]);
    this.mode = 'reschdule';
    this.modal.$$openOpeartion.set(false);
  }

}